import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, RefreshCcw, Info, AlertCircle } from "lucide-react";

const fmt$ = (c) => `$${((c || 0) / 100).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`;
const RATE_BASES = ["hourly", "daily", "m2", "m", "each", "point", "fixed"];
const COVERAGE = [
  { v: "whole_trade",    l: "Whole trade (project)" },
  { v: "category",       l: "Category" },
  { v: "components",     l: "Specific components" },
  { v: "custom_scope",   l: "Custom scope items" },
];


export default function ProjectPricingPanel({ projectId, sceneVersions = [] }) {
  const [tab, setTab] = useState("quotes");
  return (
    <div className="luxe-card p-4" data-testid="project-pricing-panel">
      <div className="flex gap-4 border-b border-neutral-200 mb-4 -mx-4 px-4">
        {[
          { k: "quotes",    l: "Trade Quotes",       tid: "tab-project-quotes" },
          { k: "estimates", l: "Estimate History",   tid: "tab-estimate-history" },
          { k: "compare",   l: "Compare Options",    tid: "tab-compare-options" },
        ].map(({ k, l, tid }) => (
          <button key={k} data-testid={tid}
                    onClick={() => setTab(k)}
                    className={`text-xs uppercase tracking-widest pb-3 border-b-2 -mb-px transition ${
                      tab === k ? "border-neutral-900 text-neutral-900 font-medium"
                                : "border-transparent text-neutral-500 hover:text-neutral-800"
                    }`}>{l}</button>
        ))}
      </div>
      {tab === "quotes"    && <ProjectTradeQuotes projectId={projectId} sceneVersions={sceneVersions}/>}
      {tab === "estimates" && <EstimateHistory projectId={projectId}/>}
      {tab === "compare"   && <CompareOptionsPricing projectId={projectId} sceneVersions={sceneVersions}/>}
    </div>
  );
}


// ── Project Trade Quotes ────────────────────────────────────────────
function ProjectTradeQuotes({ projectId, sceneVersions }) {
  const [rows, setRows]     = useState([]);
  const [trades, setTrades] = useState([]);
  const [show, setShow]     = useState(false);

  const load = useCallback(async () => {
    const [q, t] = await Promise.all([
      api.get(`/projects/${projectId}/trade-quotes`),
      api.get("/trades"),
    ]);
    setRows(q.data); setTrades(t.data);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="project-trade-quotes">
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs text-neutral-500">
          Project-specific quotes override rate cards for this project only.
        </div>
        <button data-testid="btn-add-project-quote" onClick={() => setShow(true)}
                  className="text-xs px-3 py-1.5 bg-neutral-900 text-white rounded-md hover:bg-neutral-800 inline-flex items-center gap-1">
          <Plus className="w-3 h-3"/>Add Quote
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-neutral-500 p-4 border border-dashed border-neutral-200 rounded-md text-center">
          No project quotes yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="text-neutral-500 uppercase text-[10px]">
              <tr>
                <th className="text-left px-2 py-2">Trade</th>
                <th className="text-left px-2 py-2">Contractor</th>
                <th className="text-left px-2 py-2">Basis</th>
                <th className="text-left px-2 py-2">Amount</th>
                <th className="text-left px-2 py-2">Coverage</th>
                <th className="text-left px-2 py-2">Applies</th>
                <th className="text-left px-2 py-2">Expiry</th>
                <th className="text-left px-2 py-2">Ref</th>
                <th className="text-left px-2 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-neutral-100"
                    data-testid={`project-quote-row-${r.id}`}>
                  <td className="px-2 py-2">
                    {(trades.find((t) => t.id === r.trade_id) || {}).name || r.trade_id}
                  </td>
                  <td className="px-2 py-2">{r.contractor_name || "—"}</td>
                  <td className="px-2 py-2">{r.rate_basis}</td>
                  <td className="px-2 py-2 font-medium">{fmt$(r.amount_cents)}</td>
                  <td className="px-2 py-2">{r.coverage_type}</td>
                  <td className="px-2 py-2">
                    {r.applies_to === "option_specific"
                      ? `Option ${(sceneVersions.find((s) => s.id === r.scene_version_id) || {}).version_number || "?"}`
                      : "All Options"}
                  </td>
                  <td className="px-2 py-2 text-neutral-500">{r.expiry_date || "—"}</td>
                  <td className="px-2 py-2 text-neutral-500">{r.quote_reference || "—"}</td>
                  <td className="px-2 py-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] ${
                      r.status === "active"    ? "bg-emerald-50 text-emerald-700"
                        : r.status === "superseded" ? "bg-neutral-100 text-neutral-500"
                        : "bg-amber-50 text-amber-700"
                    }`}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {show && (
        <ProjectQuoteModal projectId={projectId} trades={trades} sceneVersions={sceneVersions}
                            onClose={() => setShow(false)} onSaved={load}/>
      )}
    </div>
  );
}

function ProjectQuoteModal({ projectId, trades, sceneVersions, onClose, onSaved }) {
  const [f, setF] = useState({
    trade_id: "", rate_basis: "fixed", amount: "",
    contractor_name: "", quote_reference: "", quote_date: new Date().toISOString().slice(0, 10),
    expiry_date: "", scope_description: "",
    applies_to: "project_wide", scene_version_id: "",
    coverage_type: "whole_trade", pricing_effect: "replace",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    if (!f.trade_id) return toast.error("Trade required");
    if (!f.amount || Number(f.amount) <= 0) return toast.error("Amount required");
    if (f.applies_to === "option_specific" && !f.scene_version_id) {
      return toast.error("Option-specific quote requires a Design Option");
    }
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/trade-quotes`, {
        trade_id: f.trade_id,
        rate_basis: f.rate_basis,
        amount_cents: Math.round(Number(f.amount) * 100),
        contractor_name: f.contractor_name || undefined,
        quote_reference: f.quote_reference || undefined,
        quote_date: f.quote_date || undefined,
        expiry_date: f.expiry_date || undefined,
        scope_description: f.scope_description || undefined,
        applies_to: f.applies_to,
        scene_version_id: f.applies_to === "option_specific" ? f.scene_version_id : undefined,
        coverage_type: f.coverage_type,
        pricing_effect: f.rate_basis === "fixed" ? f.pricing_effect : undefined,
      });
      toast.success("Quote added"); onSaved(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4"
          data-testid="modal-add-project-quote" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-medium mb-4">Add Project Trade Quote</h2>
        <div className="grid grid-cols-2 gap-3">
          <F label="Trade">
            <select data-testid="input-quote-trade" value={f.trade_id}
                      onChange={(e) => set("trade_id", e.target.value)}
                      className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm">
              <option value="">Select trade…</option>
              {trades.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </F>
          <F label="Basis">
            <select data-testid="input-quote-basis" value={f.rate_basis}
                      onChange={(e) => set("rate_basis", e.target.value)}
                      className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm">
              {RATE_BASES.map((b) => <option key={b}>{b}</option>)}
            </select>
          </F>
          <F label={f.rate_basis === "fixed" ? "Fixed amount ($)" : "Rate ($)"}>
            <input data-testid="input-quote-amount" type="number" step="0.01"
                      value={f.amount} onChange={(e) => set("amount", e.target.value)}
                      className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
          </F>
          <F label="Contractor / Supplier">
            <input data-testid="input-quote-contractor"
                      value={f.contractor_name} onChange={(e) => set("contractor_name", e.target.value)}
                      className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
          </F>
          <F label="Quote reference">
            <input value={f.quote_reference} onChange={(e) => set("quote_reference", e.target.value)}
                      className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
          </F>
          <F label="Quote date">
            <input type="date" value={f.quote_date} onChange={(e) => set("quote_date", e.target.value)}
                      className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
          </F>
          <F label="Expiry date (optional)">
            <input type="date" value={f.expiry_date} onChange={(e) => set("expiry_date", e.target.value)}
                      className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
          </F>
          <F label="Coverage">
            <select data-testid="input-quote-coverage" value={f.coverage_type}
                      onChange={(e) => set("coverage_type", e.target.value)}
                      className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm">
              {COVERAGE.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </F>
          <F label="Applies to">
            <select data-testid="input-quote-applies-to" value={f.applies_to}
                      onChange={(e) => set("applies_to", e.target.value)}
                      className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm">
              <option value="project_wide">Whole project</option>
              <option value="option_specific">Specific Design Option</option>
            </select>
          </F>
          {f.applies_to === "option_specific" && (
            <F label="Design Option">
              <select data-testid="input-quote-scene-version"
                        value={f.scene_version_id} onChange={(e) => set("scene_version_id", e.target.value)}
                        className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm">
                <option value="">Select…</option>
                {sceneVersions.map((sv) => (
                  <option key={sv.id} value={sv.id}>
                    Option V{sv.version_number} · {sv.name || "(no name)"}
                  </option>
                ))}
              </select>
            </F>
          )}
          {f.rate_basis === "fixed" && (
            <F label="Effect">
              <select value={f.pricing_effect} onChange={(e) => set("pricing_effect", e.target.value)}
                        className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm">
                <option value="replace">Replace normal labour scope</option>
                <option value="additive">Additional (does NOT replace)</option>
              </select>
            </F>
          )}
        </div>
        <F label="Scope description">
          <textarea rows={2} data-testid="input-quote-scope"
                      value={f.scope_description} onChange={(e) => set("scope_description", e.target.value)}
                      placeholder="e.g. Complete media-wall electrical package"
                      className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
        </F>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm">Cancel</button>
          <button data-testid="btn-save-project-quote" disabled={saving} onClick={save}
                    className="px-4 py-2 bg-neutral-900 text-white rounded-md text-sm hover:bg-neutral-800 disabled:opacity-50">
            {saving ? "Saving…" : "Save Quote"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Estimate History + Reprice ─────────────────────────────────────
function EstimateHistory({ projectId }) {
  const [rows, setRows]     = useState([]);
  const [loading, setL]     = useState(true);
  const [selected, setSel]  = useState(null);
  const [preview, setPrev]  = useState(null);
  const [busy, setBusy]     = useState(false);

  const load = useCallback(async () => {
    setL(true);
    const r = await api.get(`/projects/${projectId}/estimate-versions`);
    setRows(r.data); setL(false);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const runPreview = async (eid) => {
    setBusy(true); setSel(eid);
    try {
      const r = await api.post(`/projects/${projectId}/estimate-versions/${eid}/reprice-preview`);
      setPrev(r.data);
    } catch (e) { toast.error("Preview failed"); }
    finally { setBusy(false); }
  };

  const applyUpdate = async () => {
    if (!selected) return;
    if (!window.confirm("Create a new Estimate Version at current pricing? The old version remains historical.")) return;
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/estimate-versions/${selected}/apply-updated-pricing`);
      toast.success("New Estimate Version created");
      setPrev(null); setSel(null); await load();
    } catch (e) { toast.error("Apply failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <Loader2 className="w-4 h-4 animate-spin"/>;
  if (rows.length === 0) return (
    <div className="text-xs text-neutral-500 p-4 border border-dashed border-neutral-200 rounded-md">
      No Estimate Versions yet. Price a Design Option to create one.
    </div>
  );

  return (
    <div data-testid="estimate-history">
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="border border-neutral-200 rounded-md p-3"
                data-testid={`estimate-row-${r.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div>
                <div className="text-sm font-medium">
                  {fmt$((r.totals || {}).sell_inc_gst_cents || 0)}
                  <span className="text-neutral-400 font-normal ml-2 text-xs">inc GST</span>
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  {new Date(r.created_at).toLocaleString("en-AU")}
                  {r.supersedes_id && <span className="ml-2 text-amber-700">· supersedes {r.supersedes_id}</span>}
                </div>
                <div className="text-[10px] text-neutral-400 mt-0.5 font-mono">
                  {r.id} · scene {r.scene_hash?.slice(0, 8)}…
                </div>
              </div>
              <button data-testid={`btn-reprice-preview-${r.id}`} onClick={() => runPreview(r.id)}
                        disabled={busy}
                        className="text-xs px-3 py-1.5 border border-neutral-300 rounded-md hover:bg-neutral-50 inline-flex items-center gap-1">
                <RefreshCcw className="w-3 h-3"/>Check for updates
              </button>
            </div>
          </div>
        ))}
      </div>

      {preview && (
        <RepricePreviewModal preview={preview}
                              onClose={() => { setPrev(null); setSel(null); }}
                              onApply={applyUpdate} busy={busy}/>
      )}
    </div>
  );
}

function RepricePreviewModal({ preview, onClose, onApply, busy }) {
  const changed = preview.changed;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4"
          data-testid="reprice-preview-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-medium mb-3">Review Price Changes</h2>
        {!changed ? (
          <div className="text-sm text-emerald-700 flex items-center gap-2">
            <Info className="w-4 h-4"/>No updated pricing — this estimate matches current rates and recipes.
          </div>
        ) : (
          <>
            <div className="flex gap-6 text-sm mb-4 border-b border-neutral-200 pb-4">
              <div><div className="text-xs text-neutral-500">Current</div>
                    <div className="font-medium">{fmt$(preview.old_total_inc_gst_cents)}</div></div>
              <div><div className="text-xs text-neutral-500">Updated</div>
                    <div className="font-medium">{fmt$(preview.new_total_inc_gst_cents)}</div></div>
              <div><div className="text-xs text-neutral-500">Delta</div>
                    <div className={`font-medium ${preview.delta_inc_gst_cents > 0 ? "text-red-700" : preview.delta_inc_gst_cents < 0 ? "text-emerald-700" : ""}`}>
                      {preview.delta_inc_gst_cents > 0 ? "+" : ""}{fmt$(preview.delta_inc_gst_cents)}
                    </div></div>
            </div>
            <table className="w-full text-xs mb-6" data-testid="reprice-diff-table">
              <thead className="text-neutral-500">
                <tr>
                  <th className="text-left py-1">Line</th>
                  <th className="text-left py-1">Change</th>
                  <th className="text-right py-1">Old</th>
                  <th className="text-right py-1">New</th>
                  <th className="text-right py-1">Delta</th>
                </tr>
              </thead>
              <tbody>
                {preview.diffs.filter((d) => d.delta_cents || d.source_changed || d.kind !== "modified").map((d) => (
                  <tr key={d.component_id} className="border-t border-neutral-100"
                      data-testid={`reprice-diff-row-${d.component_id}`}>
                    <td className="py-2">{d.name}</td>
                    <td className="py-2 text-neutral-600">
                      {d.source_changed && (
                        <div>Source: <span className="capitalize">{(d.old_applied_rate_source || "—").replace(/_/g," ")}</span>
                          → <span className="capitalize">{(d.new_applied_rate_source || "—").replace(/_/g," ")}</span></div>
                      )}
                      {d.old_applied_rate_cents !== d.new_applied_rate_cents && (
                        <div>Rate: {fmt$(d.old_applied_rate_cents || 0)} → {fmt$(d.new_applied_rate_cents || 0)}</div>
                      )}
                      {d.old_quantity !== d.new_quantity && (
                        <div>Qty: {d.old_quantity} → {d.new_quantity}</div>
                      )}
                      {d.kind === "added" && <div>New line</div>}
                      {d.kind === "removed" && <div>Removed</div>}
                    </td>
                    <td className="py-2 text-right">{fmt$(d.old_sell_ex_gst_cents)}</td>
                    <td className="py-2 text-right">{fmt$(d.new_sell_ex_gst_cents)}</td>
                    <td className={`py-2 text-right ${d.delta_cents > 0 ? "text-red-700" : d.delta_cents < 0 ? "text-emerald-700" : ""}`}>
                      {d.delta_cents > 0 ? "+" : ""}{fmt$(d.delta_cents || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(preview.new_issues || []).length > 0 && (
              <div className="mb-6 space-y-1">
                {preview.new_issues.map((i, idx) => (
                  <div key={idx} className="text-xs text-amber-700 flex items-start gap-2">
                    <AlertCircle className="w-3 h-3 mt-0.5"/><span>{i.message}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <div className="flex justify-end gap-2">
          <button data-testid="btn-keep-current" onClick={onClose} className="px-4 py-2 text-sm">
            Keep Current Pricing
          </button>
          {changed && (
            <button data-testid="btn-apply-updated" onClick={onApply} disabled={busy}
                      className="px-4 py-2 bg-neutral-900 text-white rounded-md text-sm hover:bg-neutral-800 disabled:opacity-50">
              {busy ? "Applying…" : "Apply Updated Pricing"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Compare Options ────────────────────────────────────────────────
function CompareOptionsPricing({ projectId, sceneVersions }) {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [clientSafe, setClientSafe] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!a || !b || a === b) return toast.error("Pick two different options");
    setBusy(true);
    try {
      const r = await api.get(`/projects/${projectId}/design-option-pricing/compare`,
                                { params: { a, b, client_safe: clientSafe ? 1 : 0 } });
      setResult(r.data);
    } catch (e) { toast.error("Compare failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="compare-options-pricing">
      <div className="flex gap-3 items-end mb-4 flex-wrap">
        <F label="Option A">
          <select data-testid="input-compare-a" value={a} onChange={(e) => setA(e.target.value)}
                    className="border border-neutral-300 rounded-md px-3 py-2 text-sm min-w-[220px]">
            <option value="">Select…</option>
            {sceneVersions.map((sv) => (
              <option key={sv.id} value={sv.id}>V{sv.version_number} · {sv.name || "(no name)"}</option>
            ))}
          </select>
        </F>
        <F label="Option B">
          <select data-testid="input-compare-b" value={b} onChange={(e) => setB(e.target.value)}
                    className="border border-neutral-300 rounded-md px-3 py-2 text-sm min-w-[220px]">
            <option value="">Select…</option>
            {sceneVersions.map((sv) => (
              <option key={sv.id} value={sv.id}>V{sv.version_number} · {sv.name || "(no name)"}</option>
            ))}
          </select>
        </F>
        <label className="text-xs flex items-center gap-1 mb-2">
          <input type="checkbox" data-testid="input-client-safe" checked={clientSafe}
                    onChange={(e) => setClientSafe(e.target.checked)}/>
          Client-safe view
        </label>
        <button data-testid="btn-run-compare" disabled={busy} onClick={run}
                  className="px-4 py-2 bg-neutral-900 text-white rounded-md text-sm hover:bg-neutral-800 disabled:opacity-50">
          {busy ? "Comparing…" : "What Changed?"}
        </button>
      </div>
      {result && <CompareResult result={result} clientSafe={clientSafe}/>}
    </div>
  );
}

function CompareResult({ result, clientSafe }) {
  const dd = result.design_delta || {};
  const inv = result.investment_delta || {};
  const pin = result.pricing_input_delta;
  return (
    <div className="space-y-6" data-testid="compare-result">
      <Section title="Design / Scope Delta">
        <ul className="text-xs space-y-1">
          <li data-testid="cmp-items-added">Items added: {dd.items_added?.length || 0}</li>
          <li data-testid="cmp-items-removed">Items removed: {dd.items_removed?.length || 0}</li>
          <li data-testid="cmp-items-modified">Items modified: {dd.items_modified?.length || 0}</li>
          <li>Scope items added: {dd.scope_added?.length || 0}</li>
          <li>Scope items removed: {dd.scope_removed?.length || 0}</li>
        </ul>
      </Section>
      <Section title="Investment Delta">
        <div className="flex gap-6 text-sm">
          <div><div className="text-xs text-neutral-500">Option A</div>
                <div className="font-medium">{fmt$(inv.a_total_inc_gst_cents)}</div></div>
          <div><div className="text-xs text-neutral-500">Option B</div>
                <div className="font-medium">{fmt$(inv.b_total_inc_gst_cents)}</div></div>
          <div><div className="text-xs text-neutral-500">Delta</div>
                <div data-testid="cmp-delta"
                      className={`font-medium ${inv.delta_inc_gst_cents > 0 ? "text-red-700" : "text-emerald-700"}`}>
                  {inv.delta_inc_gst_cents > 0 ? "+" : ""}{fmt$(inv.delta_inc_gst_cents)}
                </div></div>
        </div>
      </Section>
      {!clientSafe && pin && (
        <Section title="Pricing Input Delta (internal)">
          <ul className="text-xs space-y-1" data-testid="cmp-pricing-inputs">
            <li>Rate cards changed: {(pin.rate_card_ids.only_in_a.length + pin.rate_card_ids.only_in_b.length)}</li>
            <li>Project quotes changed: {(pin.project_quote_ids.only_in_a.length + pin.project_quote_ids.only_in_b.length)}</li>
            <li>Recipes changed: {(pin.recipe_ids.only_in_a.length + pin.recipe_ids.only_in_b.length)}</li>
            <li>Materials changed: {(pin.material_product_ids.only_in_a.length + pin.material_product_ids.only_in_b.length)}</li>
          </ul>
        </Section>
      )}
    </div>
  );
}


// ── Reusable atoms ──────────────────────────────────────────────────
function F({ label, children }) {
  return (
    <label className="block"><span className="text-xs text-neutral-600">{label}</span>
      <div className="mt-1">{children}</div></label>
  );
}
function Section({ title, children }) {
  return (
    <div>
      <div className="overline text-[10px] text-neutral-500 mb-2">{title}</div>
      {children}
    </div>
  );
}
